import { useMemo } from 'react';

/* An isometric terrain slab: a height field over a grid, projected and shaded
   in monochrome. Each MLAP layer has its own relief — rolling ground, a
   ridge, terraces, a channel, peaks, a crater — so the stack reads as six
   different landscapes rather than six copies of one shape. Light comes from
   the upper left; nothing is coloured. */

export type Relief = 'rolling' | 'ridge' | 'terraces' | 'channel' | 'peaks' | 'crater';

const N = 12;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

function height(relief: Relief, u: number, v: number): number {
  switch (relief) {
    case 'rolling': return 8 + 7 * Math.sin(u * Math.PI * 1.6 + 0.4) * Math.cos(v * Math.PI * 1.3) + 5 * Math.sin((u + v) * Math.PI * 1.1);
    case 'ridge': return 4 + 16 * Math.exp(-Math.pow((u - v + 0.1) * 3.2, 2)) + 3 * Math.sin(u * 7) * Math.cos(v * 5);
    case 'terraces': { const raw = 6 + 12 * Math.sin(u * 2.6 + 0.3) + 8 * Math.cos(v * 2.1); return 3 + Math.round(clamp(raw, 0, 24) / 5) * 5; }
    case 'channel': return 6 + 12 * (1 - Math.exp(-Math.pow((u - 0.45 - 0.15 * Math.sin(v * 4)) * 4, 2))) + 2.5 * Math.sin(v * 6);
    case 'peaks': return 3 + 17 * Math.abs(Math.sin(u * Math.PI * 2.2)) * Math.abs(Math.sin(v * Math.PI * 1.7));
    case 'crater': { const r2 = (Math.pow(u - 0.5, 2) + Math.pow(v - 0.5, 2)) * 9; return 4 + 10 * Math.exp(-Math.pow(r2 - 1.1, 2) * 3) + 8 * (1 - Math.exp(-r2 * 1.2)) - 7 * Math.exp(-r2 * 4); }
  }
}

function shade(t: number, lit: boolean): string {
  // lit: ink range; unlit: paper range. t in 0..1, 1 = brightest.
  const [lo, hi] = lit ? [38, 118] : [212, 248];
  const g = Math.round(lo + (hi - lo) * clamp(t, 0, 1));
  return `rgb(${g},${g},${g})`;
}

/* A point on a slab's surface, for things that sit on the terrain (the pixel
   wave in "Bring the material"). u, v in 0..1 across the grid. */
export function surfacePoint(relief: Relief, cx: number, cy: number, w: number, d: number, u: number, v: number, amp = 1) {
  const h = Math.max(0, height(relief, u, v)) * amp;
  return { X: cx + (u - v) * (w / 2), Y: cy + (u + v) * (d / 2) - d / 2 - h };
}

export interface TerrainProps { relief: Relief; cx: number; cy: number; w: number; d: number; t: number; lit: boolean; ghost?: boolean; amp?: number }

export function Terrain({ relief, cx, cy, w, d, t, lit, ghost = false, amp = 1 }: TerrainProps) {
  const { quads, cliffL, cliffR } = useMemo(() => {
    const z: number[][] = [];
    for (let x = 0; x <= N; x++) { z[x] = []; for (let y = 0; y <= N; y++) z[x]![y] = Math.max(0, height(relief, x / N, y / N)) * amp; }
    const iso = (x: number, y: number, h: number) => ({ X: cx + (x - y) * (w / (2 * N)), Y: cy + (x + y) * (d / (2 * N)) - d / 2 - h });
    const quads: { d: string; t: number }[] = [];
    for (let sum = 0; sum < 2 * N; sum++) {              // back to front
      for (let x = 0; x < N; x++) {
        const y = sum - x; if (y < 0 || y >= N) continue;
        const a = iso(x, y, z[x]![y]!), b = iso(x + 1, y, z[x + 1]![y]!), c = iso(x + 1, y + 1, z[x + 1]![y + 1]!), e = iso(x, y + 1, z[x]![y + 1]!);
        const gx = (z[x + 1]![y]! + z[x + 1]![y + 1]! - z[x]![y]! - z[x]![y + 1]!) / 2;
        const gy = (z[x]![y + 1]! + z[x + 1]![y + 1]! - z[x]![y]! - z[x + 1]![y]!) / 2;
        const light = 0.55 - 0.045 * gx + 0.03 * gy;
        quads.push({ d: `M${a.X.toFixed(1)},${a.Y.toFixed(1)} L${b.X.toFixed(1)},${b.Y.toFixed(1)} L${c.X.toFixed(1)},${c.Y.toFixed(1)} L${e.X.toFixed(1)},${e.Y.toFixed(1)} Z`, t: light });
      }
    }
    // Cliff faces along the two near edges, dropping by t.
    const edgeL: string[] = [], edgeR: string[] = [];
    for (let x = 0; x <= N; x++) { const p = iso(x, N, z[x]![N]!); edgeL.push(`${p.X.toFixed(1)},${p.Y.toFixed(1)}`); }
    for (let y = N; y >= 0; y--) { const p = iso(N, y, z[N]![y]!); edgeR.push(`${p.X.toFixed(1)},${p.Y.toFixed(1)}`); }
    const down = (pts: string[]) => pts.map(s => { const [X, Y] = s.split(',').map(Number); return `${X},${(Y! + t).toFixed(1)}`; }).reverse();
    return { quads, cliffL: `M${edgeL.join(' L')} L${down(edgeL).join(' L')} Z`, cliffR: `M${edgeR.join(' L')} L${down(edgeR).join(' L')} Z` };
  }, [relief, cx, cy, w, d, t, amp]);

  const stroke = lit ? 'rgba(255,255,255,0.18)' : ghost ? 'rgba(154,154,162,0.25)' : 'rgba(154,154,162,0.45)';
  return (
    <g>
      <path d={cliffL} fill={lit ? '#2b2b2f' : ghost ? '#EDEDEF' : '#DCDCE0'} />
      <path d={cliffR} fill={lit ? '#18181B' : ghost ? '#E6E6E9' : '#CFCFD4'} />
      {quads.map((q, i) => <path key={i} d={q.d} fill={shade(q.t, lit)} stroke={stroke} strokeWidth="0.6" style={{ transition: 'fill .5s' }} />)}
    </g>
  );
}
